import Styles from './StatusPill.module.scss'
import cx from 'classnames'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { ScheduledPostTarget } from 'types'
import { AlertTriangle } from 'blaze-ui/icons/20/AlertTriangle'
import { pluralizeWithNumber } from 'utils'

interface StatusPillProps {
  postStatus?: PostStatusPropertyValue | string | null
  isPosting?: boolean
  isUnscheduled?: boolean
  isInReview?: boolean
  scheduledPostTargets?: ScheduledPostTarget[]
  hasContentWarning?: boolean
  accountsToConnectCount?: number
}

type AlertVariant = 'error' | 'warning' | null

const deriveAlertVariant = ({
  postStatus,
  displayStatus,
  hasFailed,
  hasPublished,
  hasContentWarning,
}: {
  postStatus?: PostStatusPropertyValue | string | null
  displayStatus: PostStatusPropertyValue | string
  hasFailed: boolean
  hasPublished: boolean
  hasContentWarning?: boolean
}): AlertVariant => {
  if (displayStatus === PostStatusPropertyValue.Posted && hasFailed && hasPublished) return 'error'
  if (postStatus === PostStatusPropertyValue.AutoPostScheduled && hasContentWarning) return 'warning'

  return null
}

const AlertBadge = ({ variant }: { variant: Exclude<AlertVariant, null> }) => (
  <span
    data-testid="status-pill-alert-badge"
    data-variant={variant}
    className={cx(Styles.badge, variant === 'error' ? Styles.badgeError : Styles.badgeWarning)}
  >
    <AlertTriangle size={12} />
  </span>
)

export const StatusPill = ({
  postStatus,
  isPosting,
  isUnscheduled,
  isInReview,
  scheduledPostTargets,
  hasContentWarning,
  accountsToConnectCount,
}: StatusPillProps) => {
  if (isPosting) {
    return <div className={cx(Styles.pill, Styles.review)}>Posting...</div>
  }

  if (!postStatus) return null

  if (postStatus === PostStatusPropertyValue.AutoPostScheduled && (accountsToConnectCount ?? 0) > 0 && !isInReview) {
    return (
      <div data-testid="status-pill-accounts-to-connect" className={cx(Styles.pill, Styles.accountsToConnect)}>
        {pluralizeWithNumber(accountsToConnectCount as number, 'Account')} to Connect
      </div>
    )
  }

  const hasPublished = scheduledPostTargets?.some((t) => t.status === 'published') ?? false
  const hasFailed = scheduledPostTargets?.some((t) => t.status === 'failed') ?? false

  let displayStatus = postStatus
  if (scheduledPostTargets?.length) {
    if (hasFailed && !hasPublished) {
      displayStatus = PostStatusPropertyValue.Failed
    } else if (hasPublished) {
      displayStatus = PostStatusPropertyValue.Posted
    }
  }

  const alertVariant = deriveAlertVariant({
    postStatus,
    displayStatus,
    hasFailed,
    hasPublished,
    hasContentWarning,
  })

  const renderPill = (className: string, label: string) => (
    <div className={cx(Styles.pill, className)}>
      {label}
      {alertVariant && <AlertBadge variant={alertVariant} />}
    </div>
  )

  switch (displayStatus) {
    case PostStatusPropertyValue.Draft:
      if (isUnscheduled) {
        return renderPill(Styles.draft, 'Draft')
      }
      return renderPill(Styles.review, 'Review')

    case PostStatusPropertyValue.AutoPostScheduled:
      if (isInReview) return renderPill(Styles.review, 'Review')
      return renderPill(Styles.approved, 'Approved')

    case PostStatusPropertyValue.Posted:
      return renderPill(Styles.posted, 'Posted')

    case PostStatusPropertyValue.Failed:
      return renderPill(Styles.failed, 'Failed')

    case PostStatusPropertyValue.ReminderScheduled:
      return renderPill(Styles.review, 'Reminder Set')

    default:
      return renderPill(Styles.review, 'Review')
  }
}
